
    import { mapState, mapActions, mapMutations } from 'vuex';
    import Cookie from 'js-cookie';

    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import {
        faQuestionCircle as farQuestionCircle,
        faSquare as farSquare,
        faFileArrowUp as farFileArrowUp,
        faCheckCircle as farCheckCircle,
    } from '@fortawesome/pro-regular-svg-icons';

    import {
        faCheckSquare as fasCheckSquare,
    } from '@fortawesome/pro-solid-svg-icons';

    import C1Dropdown from '@/C1Components/C1Dropdown';
    import C1Spinner from '@/C1Components/C1Spinner';

    export default {
        name: 'SeekerResumeUpload',

        components: {
            C1Dropdown,
            C1Spinner,
            FontAwesomeIcon,
        },

        props: {
            maxHeight: {
                type: String,
                default: '440px',
            },
            maxWidth: {
                type: String,
                default: '358px',
            },
            hideBrowseButton: {
                type: Boolean,
                default: false,
            },
            hideProfileVisibility: {
                type: Boolean,
                default: false,
            },
            noResumeReview: {
                type: Boolean,
                default: false,
            },
            hideUploadButton: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                seekerApiBaseUrl: process.env.API_URL,
                seekerToken: Cookie.get(process.env.COOKIE_TOKEN_NAME),
                dropzone: '',
                upload: {
                    acceptedFiles: '.pdf,.doc,.docx,.rtf,.txt',
                    maxFileSize: 10,
                    fileTypesLabel: 'pdf, doc, docx, rtf, txt',
                    maxMb: '10 MB',
                    errorMessage: '',
                },
                spinner: {
                    is_uploading: false,
                    is_uploaded: false,
                },
                visibilities: [
                    { id: 1, label: 'Visible' },
                    { id: 5, label: 'Confidential' },
                ],
                form: {
                    seeker_visiblity: '',
                    top_resume: true,
                    resume: '',
                    resume_quality: '',
                },
                icons: {
                    fasCheckSquare,
                    farQuestionCircle,
                    farSquare,
                    farFileArrowUp,
                    farCheckCircle,
                },
                lastUploadedResume: {},
            };
        },

        computed: {
            ...mapState({
                $_me: state => state.me.user,
            }),

            isUploadNotReady() {
                return !this.isDocumentUpload && !this.isDocumentUploading && !this.isDocumentUploaded;
            },

            isUploadReady() {
                return this.isDocumentUpload && !this.isDocumentUploading && !this.isDocumentUploaded;
            },

            isUploaded() {
                return this.isDocumentUploaded;
            },

            isDocumentUpload() {
                return this.form.resume.name || false;
            },

            isDocumentUploading() {
                return this.spinner.is_uploading;
            },

            isDocumentUploaded() {
                return this.spinner.is_uploaded;
            },
        },

        watch: {
            'spinner.is_uploading'(value) {
                this.$emit('uploading', value);
            },
            'upload.errorMessage'(value) {
                this.$emit('error', value);
            },
        },

        created() {
            this.form.seeker_visiblity = this.visibilities[0];
        },

        mounted() {
            const dropzoneScript = document.createElement('script');
            dropzoneScript.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/dropzone/5.9.3/min/dropzone.min.js');
            document.head.appendChild(dropzoneScript);
            dropzoneScript.onload = () => {
                this.$nextTick(() => {
                    this.initDropzone();
                });
            };
        },

        beforeDestroy() {
            if (typeof this.dropzone.destroy !== 'function') {
                return;
            }
            this.dropzone.destroy();
        },

        methods: {
            ...mapActions('me', {
                $_storeResume: 'storeResume',
                $_updateResume: 'updateResume',
                $_deleteResume: 'deleteResume',
                $_downloadResume: 'downloadResume',
                $_getMe: 'getMe',
            }),

            ...mapMutations('me', {
                $_PUT_RESUME: 'PUT_RESUME',
            }),

            initDropzone() {
                window.Dropzone.autoDiscover = false;
                const vm = this;
                const options = {
                    url: `${this.seekerApiBaseUrl}/v1/me/resumes`,
                    maxFiles: 1,
                    addRemoveLinks: false,
                    uploadMultiple: false,
                    maxFilesize: this.upload.maxFileSize,
                    autoProcessQueue: false,
                    acceptedFiles: this.upload.acceptedFiles,
                    createImageThumbnails: false,
                    init() {
                        const submitButton = vm.$el.querySelector('#resumeUploadButton');

                        submitButton.addEventListener('click', () => {
                            vm.spinner.is_uploading = true;
                            this.processQueue();
                        });

                        this.on('error', (file, response) => {
                            vm.upload.errorMessage = 'Something went wrong on our end and has been notified to our technical team. Please try again later.';
                            if (!file.accepted) {
                                vm.upload.errorMessage = response?.errors?.file[0] || response;
                            }
                            vm.form.resume = {};
                            this.removeAllFiles(true);
                            vm.spinner.is_uploading = false;
                        });

                        this.on('sending', (file, xhr, formData) => {
                            formData.append('visibility_id', vm.form.seeker_visiblity.id || 1);
                            formData.append('top_resume', !vm.noResumeReview && vm.form.top_resume ? 1 : 0);
                        });

                        this.on('addedfile', (file) => {
                            vm.form.resume = file;
                            console.log(vm.form.resume);
                            vm.$emit('fileAdded', file);
                        });

                        this.on('remove', (file) => {
                            vm.form.resume = {};
                            vm.$emit('change');
                        });

                        this.on('accept', (file, done) => {
                            vm.errorMessage = '';
                            vm.spinner.is_uploading = true;
                            done();
                        });

                        this.on('success', (file, response) => {
                            vm.spinner.is_uploading = false;
                            vm.spinner.is_uploaded = true;
                            this.removeFile(file);
                            vm.onUploadSuccess(response);
                        });
                    },
                };

                if (this.seekerToken) {
                    options.headers = {
                        Authorization: `Bearer ${this.seekerToken}`,
                    };
                }

                this.dropzone = new window.Dropzone(this.$el.querySelector('#dropzone'), options);
            },

            dzClick() {
                this.dropzone.hiddenFileInput.click();
            },

            removeDocumentUpload() {
                this.form.resume = {};
                this.upload.errorMessage = '';
                this.dropzone.removeAllFiles(true);
            },

            async onUploadSuccess(response) {
                this.$_PUT_RESUME(response.data);
                await this.$_getMe().then(() => {
                    const resume = response.data;
                    this.form.resume = resume;
                    this.form.resume_quality = resume.resume_quality_percentage ? resume.resume_quality_percentage : '';
                    this.$emit('promptSuccess');
                });
            },

            onDone() {
                this.$emit('onDone');
            },

            promptClose() {
                this.$emit('promptClose');
            },

            bytesToSize(bytes) {
                const mb = bytes / 1024 / 1024;
                const kb = bytes / 1024;
                return mb > 1 ? `${mb.toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
            },

            triggerUpload() {
                this.$refs.resumeUploadButton.click();
            },
        },
    };
